import React, { useContext, useEffect, useMemo, useState } from 'react';
import { CloudProviderContext, shouldShowAzurePlaceholderView } from "../../CloudProviderContext";
import { useDispatch, useSelector } from 'react-redux';
import { Router, Switch, Route, useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';
import { Box, Tab, Tabs } from '@mui/material';
import {
  Container,
  Card,
  CardBody,
  Modal,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { AccountDetailThemeProvider } from '../../components/AccountDetailPolicy/accountDetailTheme';
import { ACCOUNT_DETAIL_FONT_SX } from '../../components/AccountDetailPolicy/accountDetailTypography';
import {
  getLicensingData,
  getListOfAccts,
  pushNotification,
  getProfileRoleInfo,
  getListOfOrgsAndAccts,
  getListAccRules,
  getListAllRules,
} from '../../store/actions';
import AccountsConfig from './_accountsConfig';
import UsersConfig from './_usersConfig';
import SecurityConfig from './_securityConfig';
import ResourceGroupsConfig from "./_resourceGroupsConfig";
import Suppression from './Suppression';
import InaccessibleResources from '../../helpers/InaccessibleResources';
import OfflineCur from "../OfflineCur";
import SsoConfig from "./SsoConfig";
import getUserRole from "../../helpers/jwt-token-access/userRole";

const freeLicenseName = process.env.REACT_APP_FREE_ORGANIZATION_LICENSE;
const freeLicenses = [freeLicenseName];

const AZURE_DISABLED_TAB_STYLE = {
  opacity: 0.5,
  filter: "blur(1px)",
  pointerEvents: "none",
  cursor: "not-allowed",
};

const CONFIG_TABS_SX = {
  minHeight: 40,
  width: "100%",
  "& .MuiTabs-flexContainer": {
    width: "100%",
    alignItems: "center",
    gap: 0.25,
  },
  "& .MuiTab-root": {
    textTransform: "none",
    minHeight: 40,
    minWidth: "max-content",
    flexShrink: 0,
    flexGrow: { xs: 0, md: 1 },
    flexBasis: { xs: "auto", md: 0 },
    maxWidth: "none",
    px: { xs: 1, sm: 1.25 },
    fontSize: { xs: "0.75rem", md: "0.8125rem" },
    color: "#6b7280",
    fontWeight: 500,
    borderRadius: "8px",
    whiteSpace: "nowrap",
  },
  "& .MuiTab-root.Mui-selected": {
    color: "#111827 !important",
    fontWeight: 700,
    backgroundColor: "#ffffff",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "#F98125",
    height: 3,
    borderRadius: "2px 2px 0 0",
  },
  "& .MuiTabs-scrollButtons": {
    color: "#64748b",
    "&.Mui-disabled": { opacity: 0.3 },
  },
};

const Configuration = ({ licenseName }) => {
  const { selectedProvider } = useContext(CloudProviderContext);
  const isAzureView = useMemo(
    () => shouldShowAzurePlaceholderView(selectedProvider),
    [selectedProvider]
  );
  const location = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    dispatch(
      getListOfAccts({
        params: { accountType: selectedProvider },
      })
    );
  }, [selectedProvider, dispatch]);

    console.log("Configuration page, licenseName: " , licenseName);
  const defaultRoutes = [
    {
      displayName: "Cloud Accounts",
      component: AccountsConfig,
      navLink: "/configuration/accounts",
    },
    {
      displayName: "Users",
      component: UsersConfig,
      navLink: "/configuration/users",
    },
    // {
    //   displayName: "Security",
    //   component: freeLicenses.includes(licenseName)? InaccessibleResources : SecurityConfig,
    //   navLink: "/configuration/security",
    // },
    {
      displayName: "Resource Groups",
      component: freeLicenses.includes(licenseName)? InaccessibleResources : ResourceGroupsConfig,
      navLink: "/configuration/resourcegroups",
    },
    {
      displayName: "Compliance Configuration",
      component: freeLicenses.includes(licenseName)? InaccessibleResources : Suppression,
      navLink: "/configuration/suppression",
    },
    {
      displayName: "Upload CUR2.0",
      component: OfflineCur,
      navLink: "/configuration/offline-cur",
    },
  ];
  let ssoRole = "NONE";
  try {
    ssoRole = getUserRole();
  } catch (error) {
    ssoRole = "NONE";
  }
  const canAccessSso = ssoRole === "Administrator";
  if (canAccessSso) {
    defaultRoutes.push({
      displayName: "Single Sign-On",
      component: SsoConfig,
      navLink: "/configuration/sso",
    });
  }
  const visibleRoutes = defaultRoutes.filter(
    (route) => !(isAzureView && route.navLink === "/configuration/offline-cur")
  );
  const [mask, setMask] = useState(false);
  const [configRoutes, setConfigRoutes] = useState();
  const [contactForAsistance, setContactForAsistance] = useState(false);
  const [isCustomer, setIsCustomer] = useState(false);
  const licensing = useSelector((state) => state.licensing);
  const notifications = useSelector((state) => state.notify);
  const acctList = useSelector((state) => state.acctList);
  const orgAccount = useSelector((state) => state.orgAccount);
  const { data, costLookup, status } = licensing;

    useEffect(() => {
        // Check if customer is true in selectedOrganization
        const selectedOrganizationStr = localStorage.getItem('selectedOrganization');
        if (selectedOrganizationStr) {
            try {
                const selectedOrganization = JSON.parse(selectedOrganizationStr);
                setIsCustomer(selectedOrganization.customer === true);
            } catch (error) {
                console.error('Error parsing selectedOrganization:', error);
            }
        }

        if (!licensing?.data && !licensing?.loading && !licensing?.error) {
            dispatch(getLicensingData());
        }
        // if (!acctList?.accounts && !acctList?.loading) {
        //     dispatch(getListOfAccts());
        // }

        // console.log("Calling getProfileRoleInfo() from configuration.index#useEffect");
        // dispatch(getProfileRoleInfo());
        if ((!orgAccount?.accounts || !orgAccount?.organizations?.length) && !orgAccount?.loading) {
            dispatch(getListOfOrgsAndAccts());
        }
    }, []);
    useEffect(() => {
        if (licensing && licensing.data && licensing.data.numAccounts && acctList && acctList.accounts) {
            const numAccounts = licensing.data.numAccounts;
            const accountNotification = {message: `${acctList.accounts.length -1}/${numAccounts}`, 
            fullmessage:`Accounts ${acctList.accounts.length -1} of ${numAccounts} Used`,
            amend: false};
            if (acctList.accounts.length-1 < numAccounts) {
                accountNotification.className="badge badge-outline bg-white border border-secondary"
            } else if (acctList.accounts.length >= numAccounts) {
                accountNotification.className="badge-mine"
            }
            dispatch(pushNotification(accountNotification));

            const numUsers = licensing.data.numUsers;
            const temp = [];
            acctList.accounts.forEach((item) => {
                item.listusr.forEach((entity) => temp.push(entity))
            });
            const usersNotification = {message: `${acctList.usersConfig.length}/${numUsers}`, 
            fullmessage:`Users ${acctList.usersConfig.length} of ${numUsers} Used`,
            amend: true};
            if (acctList.usersConfig.length < numUsers) {
                usersNotification.className="badge badge-outline bg-white border border-secondary"
            } else if (acctList.usersConfig.length >= numUsers) {
                usersNotification.className = "badge-mine"
            }
            dispatch(pushNotification(usersNotification));
        }
    }, [licensing, acctList])

    useEffect(() => {
        const updatedRoutes = [];
        visibleRoutes.forEach((item) => {
            // Check if current path matches the route or is a sub-route
            const isActive = location.pathname === item.navLink || 
                           (item.navLink !== "/configuration/accounts" && 
                            location.pathname.startsWith(item.navLink));
            updatedRoutes.push({
                displayName: item.displayName,
                navLink: item.navLink,
                isActive: isActive,
            })
        });
        setConfigRoutes(updatedRoutes);
    }, [location, isAzureView])

    useEffect(() => {
        if (!isAzureView) return;
        if (location.pathname.startsWith("/configuration/offline-cur")) {
            history.replace("/configuration/accounts");
        }
    }, [isAzureView, location.pathname, history]);

    useEffect(() => {
        if (canAccessSso) return;
        if (location.pathname.startsWith("/configuration/sso")) {
            history.replace("/configuration/accounts");
        }
    }, [canAccessSso, location.pathname, history]);

    useEffect(() => {
        
        if (data && !data.isLicensingComplete && data.licenseName === "NONE") {
            console.log("data is here ", data)
            setMask(true)
            setContactForAsistance(true)
        } else {
            setMask(false);
        }
    }, [data])

    const handleTabChange = (_, navLink) => {
        const isDirty = sessionStorage.getItem('isDirty', 'true');
        if (navLink && !isDirty) {
            history.push(navLink);
        }
    };

    const isSsoRoute = location.pathname.startsWith("/configuration/sso");

    const configPageBody = (content) => (
        <AccountDetailThemeProvider>
            <div
                className="page-content"
                style={{
                    backgroundColor: "#fff",
                    ...(isSsoRoute
                        ? {
                            // Lock SSO to the viewport (below topbar). Footer padding stays inside via border-box.
                            height: "calc(100vh - 56px)",
                            maxHeight: "calc(100vh - 56px)",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                            boxSizing: "border-box",
                          }
                        : {}),
                }}
            >
                <Container
                    fluid
                    style={{
                        paddingLeft: 0,
                        paddingRight: 0,
                        ...(isSsoRoute
                            ? {
                                flex: 1,
                                minHeight: 0,
                                height: "100%",
                                maxHeight: "100%",
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                              }
                            : {}),
                    }}
                >
                    {content}
                </Container>
            </div>
        </AccountDetailThemeProvider>
    );

    const licensingModal = (
        <Modal isOpen={contactForAsistance}>
            <ModalBody>
                <div className="form-check">
                    <label className="form-check-label" htmlFor="agreeTerms">
                        Licensing must be completed before any changes can be made.
                    </label>
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn btn-secondary" onClick={() => setContactForAsistance(false)}>OK</button>
            </ModalFooter>
        </Modal>
    );

    // If customer is true, show only UsersConfig without tabs
    if (isCustomer) {
        return (
            <React.Fragment>
                {configPageBody(
                    <Card className={mask ? "mask" : ""}>
                        <CardBody>
                            <UsersConfig />
                            {licensingModal}
                        </CardBody>
                    </Card>
                )}
            </React.Fragment>
        )
    }

    const activeTabValue =
        (configRoutes && configRoutes.find((item) => item.isActive)?.navLink) || false;

    return (
        <React.Fragment>
            {configPageBody(
                <Box
                    className={mask ? "mask" : ""}
                    sx={{
                        pt: 1.5,
                        pb: 1,
                        width: "100%",
                        maxWidth: "100%",
                        overflowX: "hidden",
                        ...(isSsoRoute
                            ? {
                                display: "flex",
                                flexDirection: "column",
                                flex: 1,
                                minHeight: 0,
                                height: "100%",
                                maxHeight: "100%",
                                overflow: "hidden",
                                boxSizing: "border-box",
                              }
                            : {}),
                        ...ACCOUNT_DETAIL_FONT_SX,
                    }}
                >
                    {configRoutes && (
                        <Box
                            sx={{
                                width: "100%",
                                maxWidth: "100%",
                                backgroundColor: "#f3f4f6",
                                borderRadius: "10px",
                                px: 0.5,
                                py: 0.5,
                                overflow: "hidden",
                                flexShrink: 0,
                            }}
                        >
                            <Tabs
                                value={activeTabValue}
                                onChange={handleTabChange}
                                variant="scrollable"
                                scrollButtons="auto"
                                allowScrollButtonsMobile
                                sx={CONFIG_TABS_SX}
                            >
                                {configRoutes.map((item) => (
                                    <Tab
                                        key={item.navLink}
                                        value={item.navLink}
                                        label={item.displayName}
                                        disabled={item.isDisabled}
                                        sx={item.isDisabled ? AZURE_DISABLED_TAB_STYLE : undefined}
                                    />
                                ))}
                            </Tabs>
                        </Box>
                    )}
                    <Box
                        sx={{
                            pt: 2.5,
                            width: "100%",
                            maxWidth: "100%",
                            minWidth: 0,
                            ...(isSsoRoute
                                ? {
                                    flex: 1,
                                    minHeight: 0,
                                    overflow: "hidden",
                                    display: "flex",
                                    flexDirection: "column",
                                    "& > *": {
                                      flex: 1,
                                      minHeight: 0,
                                      height: "100%",
                                      maxHeight: "100%",
                                      overflow: "hidden",
                                    },
                                  }
                                : { overflowX: "hidden" }),
                        }}
                    >
                        <Switch>
                            {visibleRoutes.map((item) => (
                                <Route
                                    key={item.navLink}
                                    path={item.navLink}
                                    component={item.component}
                                />
                            ))}
                        </Switch>
                    </Box>
                    {licensingModal}
                </Box>
            )}
        </React.Fragment>
    )
}


export default Configuration;